import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  AttachFile,
  MoreVert,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import AppointmentDetailView from "./appointmentDetailView"; // Adjust the import path as needed
import { useNavigate } from "react-router-dom";
import { authenticatedFetch } from "../../../network/api";

import { DateTime } from "luxon";

const formatTimefunc = (requestedSlot) => {
  if (!requestedSlot?.date || !requestedSlot?.time) return "Invalid";

  const [hourStr, ampm] = requestedSlot.time.split(" "); // e.g., "9 AM"
  let hour = parseInt(hourStr);
  if (ampm === "PM" && hour !== 12) hour += 12;
  if (ampm === "AM" && hour === 12) hour = 0;

  const [year, month, day] = requestedSlot.date.split("-").map(Number);

  const utcDateTime = DateTime.fromObject(
    { year, month, day, hour },
    { zone: "utc" } // <-- Correct: treat DB time as UTC
  );

  const localDateTime = utcDateTime.setZone(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  return localDateTime.toLocaleString(DateTime.DATETIME_MED); // e.g., "Jun 18, 2025, 2:00 PM"
};

function getLocalTimeAgo(utcTimestamp) {
  return DateTime.fromISO(utcTimestamp, { zone: "utc" })
    .setZone(Intl.DateTimeFormat().resolvedOptions().timeZone) // converts to user's local TZ
    .toRelative(); // e.g., "4 minutes ago"
}

const RequestView = ({
  searchQuery = "",
  showOnlyUpdates = false,
  selectedFilters = {},
  appointments = [],
  loading = false,
  error = null,
  onRefresh,
}) => {
  const [sortConfig, setSortConfig] = useState({
    key: "requestedTimestamp",
    direction: "asc",
  });
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const navigate = useNavigate();
  const lawyer = useSelector((s) => s.attorneyData.attorneyData);
  const videoToken = useSelector((s) => s.attorneyData.videoToken);

  const handleAccept = async (id) => {
    try {
      const res = await authenticatedFetch("/api/attorney/webapp/approveAppointment", {
        method: "POST",
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (res.ok) {
        alert("Appointment successfully confirmed!");
        onRefresh(); // Refresh the list
      } else {
        alert("Error confirming: " + data.error);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to confirm appointment.");
    }
  };

  const handleReject = async (id) => {
    try {
      const res = await authenticatedFetch("/api/attorney/webapp/denyAppointment", {
        method: "POST",
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (res.ok) {
        alert("Appointment denied.");
        onRefresh(); // Refresh the list
      } else {
        alert("Error denying: " + data.error);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to deny appointment.");
    }
  };


  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  const handleViewDetail = (appointment) => {
    setSelectedAppointment(appointment);
  };

  const handleMenuClick = (event, appointmentId) => {
    setAnchorEl(event.currentTarget);
    setActionMenuOpen(appointmentId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActionMenuOpen(null);
  };

  const toggleExpandedRow = (appointmentId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(appointmentId)) {
      newExpanded.delete(appointmentId);
    } else {
      newExpanded.add(appointmentId);
    }
    setExpandedRows(newExpanded);
  };

  const formatTime = (timeString) => {
    return timeString;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));

    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440)
      return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  const filteredAndSortedAppointments = useMemo(() => {
    let filtered = appointments.filter((appointment) => {
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const clientName = `${appointment.client?.first_name || ""} ${
          appointment.client?.last_name || ""
        }`.toLowerCase();
        const appointmentType =
          appointment.appointmentType?.name?.toLowerCase() || "";
        const message = appointment.message?.toLowerCase() || "";

        if (
          !clientName.includes(searchLower) &&
          !appointmentType.includes(searchLower) &&
          !message.includes(searchLower)
        ) {
          return false;
        }
      }

      if (showOnlyUpdates && !appointment.ai_recommendation) {
        return false;
      }

      if (
        selectedFilters["Urgent"] &&
        !appointment.ai_reason?.includes("urgent")
      ) {
        return false;
      }

      if (
        selectedFilters["Has Document"] &&
        (!appointment.documents || appointment.documents.length === 0)
      ) {
        return false;
      }

      return true;
    });

    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case "clientName":
          aValue = `${a.client?.first_name || ""} ${a.client?.last_name || ""}`;
          bValue = `${b.client?.first_name || ""} ${b.client?.last_name || ""}`;
          break;
        case "requestedTimestamp":
          aValue = new Date(a.requestedTimestamp);
          bValue = new Date(b.requestedTimestamp);
          break;
        case "appointmentType":
          aValue = a.appointmentType?.appointment_name || "";
          bValue = b.appointmentType?.appointment_name || "";
          break;
        case "created_at":
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [appointments, searchQuery, showOnlyUpdates, selectedFilters, sortConfig]);

  const SortableTableCell = ({ column, children, sx, ...props }) => (
    <TableCell
      {...props}
      onClick={() => handleSort(column)}
      sx={{
        cursor: "pointer",
        "&:hover": { backgroundColor: "grey.50" },
        fontFamily: "Poppins",
        fontSize: "16px",
        fontWeight: 500,
        color: "#333333",
        textTransform: "uppercase",
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {children}
        {sortConfig.key === column &&
          (sortConfig.direction === "asc" ? <ExpandLess /> : <ExpandMore />)}
      </Box>
    </TableCell>
  );

  if (selectedAppointment) {
    return (
      <AppointmentDetailView
        appointment={selectedAppointment}
        appointments={filteredAndSortedAppointments}
        onBack={() => setSelectedAppointment(null)}
        onAccept={handleAccept}
        onReject={handleReject}
      />
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography color="error" variant="h6" gutterBottom>
          Error loading appointments
        </Typography>
        <Typography color="text.secondary" gutterBottom>
          {error}
        </Typography>
        <Button variant="contained" onClick={onRefresh} sx={{ mt: 2 }}>
          Try Again
        </Button>
      </Box>
    );
  }

  if (filteredAndSortedAppointments.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography variant="h5" gutterBottom>
          No consultation requests yet
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ maxWidth: "500px", mx: "auto", mb: 3 }}
        >
          You don't have any new case requests at the moment. Once a client
          submits a consultation request, it will appear here. You'll be able to
          review their intake form, uploaded files, and an AI-generated case
          summary before deciding whether to accept.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          If you're expecting a case and still don't see anything, make sure
          your filters are cleared or{" "}
          <Button variant="text" size="small">
            contact support
          </Button>
          . Scheduled appointments can still be found in your{" "}
          <Button
            onClick={() => {
              navigate("/attorney/calendar");
            }}
            variant="text"
            size="small"
          >
            calendar
          </Button>
          .
        </Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <SortableTableCell
                column="clientName"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#333333",
                }}
              >
                Client Name
              </SortableTableCell>
              <SortableTableCell
                column="created_at"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#333333",
                }}
              >
                Submitted updated
              </SortableTableCell>
              <SortableTableCell
                column="requestedTimestamp"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#333333",
                }}
              >
                Appointment Time
              </SortableTableCell>
              <TableCell
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#333333",
                  textTransform: "uppercase",
                }}
              >
                Consultation Type
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#333333",
                  textTransform: "uppercase",
                }}
              >
                Files
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#333333",
                  textTransform: "uppercase",
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredAndSortedAppointments.map((appointment) => (
                          <AppointmentRow
              key={appointment.id}
              appointment={appointment}
              isExpanded={expandedRows.has(appointment.id)}
              onToggleExpand={toggleExpandedRow}
              onViewDetail={handleViewDetail}
              onAccept={handleAccept}
              onReject={handleReject}
              formatDate={formatDate}
              formatTime={formatTime}
              getTimeAgo={getTimeAgo}
            />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// Add this component before your main RequestView component

const AppointmentRow = ({
  appointment,
  onViewDetail,
  onAccept,
  onReject,
  formatDate,
  formatTime,
  getTimeAgo,
}) => {
  return (
    <>
      {/* Main row */}
      <TableRow hover>
        {/* 1) Client Name */}
        <TableCell sx={{ borderBottom: "none", py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: "#144a8c",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              {appointment.client?.first_name?.[0]}
              {appointment.client?.last_name?.[0]}
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight={600} color="#1a1a1a">
                {appointment.client?.first_name} {appointment.client?.last_name}
              </Typography>
            </Box>
          </Box>
        </TableCell>

        {/* 2) Submitted updated */}
        <TableCell sx={{ borderBottom: "none", py: 2 }}>
          <Typography variant="body2" color="#666">
            {appointment.created_at
              ? getLocalTimeAgo(appointment.created_at)
              : "N/A"}
          </Typography>
        </TableCell>

        {/* 3) Appointment Time */}
        <TableCell sx={{ borderBottom: "none", py: 2 }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="#666">
              {formatTimefunc(appointment.requested_slot)}
            </Typography>
          </Box>
        </TableCell>

        {/* 4) Consultation Type */}
        <TableCell sx={{ borderBottom: "none", py: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Chip
              label={
                appointment.appointmentType?.appointment_name ||
                "No Apppointment Name found"
              }
              sx={{
                bgcolor: "#f5f5f5",
                color: "#333",
                fontWeight: 500,
                fontSize: "14px",
                height: "32px",
              }}
            />
            {/* Payment Status Chip */}
            {appointment.status === "authorization_hold" && (
              <Chip
                label="Payment Hold"
                size="small"
                sx={{
                  bgcolor: "#fff3cd",
                  color: "#856404",
                  fontWeight: 500,
                  fontSize: "12px",
                  height: "24px",
                }}
              />
            )}
            {appointment.status === "pending" && (
              <Chip
                label="No Payment"
                size="small"
                sx={{
                  bgcolor: "#f8d7da",
                  color: "#721c24",
                  fontWeight: 500,
                  fontSize: "12px",
                  height: "24px",
                }}
              />
            )}
          </Box>
        </TableCell>

        {/* 5) Files */}
        <TableCell sx={{ borderBottom: "none", py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AttachFile sx={{ fontSize: 18, color: "#666" }} />
            <Typography variant="body2" color="#666" fontWeight={500}>
              {appointment.documents?.length || 0}
            </Typography>
          </Box>
        </TableCell>

        {/* 6) Action */}
        {/* <TableCell sx={{ borderBottom: "none", py: 2 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              sx={{
                bgcolor: "#144a8c",
                color: "white",
                textTransform: "none",
                fontWeight: 500,
                px: 2.5,
                py: 0.75,
                "&:hover": {
                  bgcolor: "#144a7a",
                },
              }}
              onClick={() => onAccept && onAccept(appointment.id)}
            >
              Accept
            </Button>
            <Button
              variant="outlined"
              sx={{
                borderColor: "#ddd",
                color: "#666",
                textTransform: "none",
                fontWeight: 500,
                px: 2.5,
                py: 0.75,
                "&:hover": {
                  borderColor: "#bbb",
                  bgcolor: "#f9f9f9",
                },
              }}
              onClick={() => onReject && onReject(appointment.id)}
            >
              Reject
            </Button>
            <Button
              variant="outlined"
              sx={{
                borderColor: "#ddd",
                color: "#666",
                textTransform: "none",
                fontWeight: 500,
                px: 2.5,
                py: 0.75,
                "&:hover": {
                  borderColor: "#bbb",
                  bgcolor: "#f9f9f9",
                },
              }}
              onClick={() => onViewDetail && onViewDetail(appointment)}
            >
              View Detail
            </Button>
          </Box>
        </TableCell> */}
        <TableCell sx={{ borderBottom: "none", py: 2 }}>
          <MoreVert />
        </TableCell>
      </TableRow>

      {/* AI Recommendation Alert (full-width) */}
      <TableRow>
        <TableCell colSpan={6} sx={{ borderBottom: "none", p: 0 }}>
          <Alert
            severity="info"
            sx={{
              bgcolor: "#e3f2fd",
              border: "1px solid #bbdefb",
              borderRadius: "8px",
              m: 2,
              "& .MuiAlert-message": {
                width: "100%",
              },
            }}
          >
            <Box>
              <Typography
                variant="body2"
                fontWeight={600}
                color="#1976d2"
                sx={{ mb: 0.5 }}
              >
                ⚡ AI suggests:{" "}
                {appointment.ai_recommendation ||
                  "No AI recommendation available"}
              </Typography>
              <Typography variant="body2" color="#1976d2">
                {appointment.ai_reason ||
                  "No reason provided for AI recommendation."}
              </Typography>
            </Box>
          </Alert>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ borderBottom: "1px solid #e0e0e0", p: 0 }}>
          <Box
            sx={{ display: "flex", gap: 1, justifyContent: "flex-end", p: 2 }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: "#144a8c",
                color: "white",
                textTransform: "none",
                fontWeight: 500,
                px: 2.5,
                py: 0.75,
                "&:hover": {
                  bgcolor: "#144a7a",
                },
              }}
              onClick={() => onAccept && onAccept(appointment.id)}
            >
              Accept
            </Button>
            <Button
              variant="outlined"
              sx={{
                borderColor: "#ddd",
                color: "#666",
                textTransform: "none",
                fontWeight: 500,
                px: 2.5,
                py: 0.75,
                "&:hover": {
                  borderColor: "#bbb",
                  bgcolor: "#f9f9f9",
                },
              }}
              onClick={() => onReject && onReject(appointment.id)}
            >
              Reject
            </Button>
            <Button
              variant="outlined"
              sx={{
                borderColor: "#ddd",
                color: "#666",
                textTransform: "none",
                fontWeight: 500,
                px: 2.5,
                py: 0.75,
                "&:hover": {
                  borderColor: "#bbb",
                  bgcolor: "#f9f9f9",
                },
              }}
              onClick={() => onViewDetail && onViewDetail(appointment)}
            >
              View Detail
            </Button>
          </Box>
        </TableCell>
      </TableRow>
    </>
  );
};

export default RequestView;
